import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { DMSConnectionConfig, ServicesProvider } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Input type for creating a DMS connection (without id, injected from provider)
 */
export interface CreateDmsConnectionInput {
	dmsConnectionConfig: Partial<DMSConnectionConfig>;
}

/**
 * API function to create a DMS connection for a services provider
 */
export const createInventoryDmsConnection = async (
	servicesProviderId: string,
	input: CreateDmsConnectionInput,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.inventoryDmsConnection(servicesProviderId),
		input,
	);
	return response.data;
};

/**
 * Hook for creating a DMS connection for a services provider
 */
export const useCreateInventoryDmsConnection = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateDmsConnectionInput) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to create inventory DMS connection without a services provider ID.',
				);
			}
			return createInventoryDmsConnection(servicesProviderId, input);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(queries.servicesProviders.detail(data.id).queryKey, data);
		},
	});
};
